import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Modal, Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import { appendPath } from '@truqc/content-path';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import { enabledSelector, newPathSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import DocumentsUploadPicker from '../../documents/documentsUploadPicker';
const addDocs = async (onChange, selectedDocs, userID, path) => {
  const updates = selectedDocs.map((next, i) => {
    // set up a new ID for a new element:
    const id = uuidV4().toUpperCase();
    // Add ms offset so the order will be preserved
    const createdDate = moment().milliseconds(i).toISOString();
    const document = {
      _created: createdDate,
      appCreatorID: userID,
      remotePath: {
        _download: next.remotePath
      },
      displayName: next.displayName,
      cloudDocumentID: next.cloudDocumentID
    };
    return {
      document,
      contentPath: appendPath(path, id)
    };
  });
  // Dispatch an update for each collection item added
  for (let i = 0; i < updates.length; i += 1) {
    const _updates$i = updates[i],
      document = _updates$i.document,
      contentPath = _updates$i.contentPath;
    // eslint-disable-next-line no-await-in-loop
    await onChange({
      path: contentPath,
      value: document
    });
  }
};
const AddDocumentButton = _ref => {
  let documentModalJobActions = _ref.documentModalJobActions,
    _ref$documentModalJob = _ref.documentModalJob,
    progress = _ref$documentModalJob.progress,
    list = _ref$documentModalJob.list,
    _ref$jobID = _ref.jobID,
    jobID = _ref$jobID === void 0 ? null : _ref$jobID,
    enabled = _ref.enabled,
    documentModalJob = _ref.documentModalJob,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? 'defaultID' : _ref$testingIdentifie,
    title = _ref.title,
    initialIndexQuery = _ref.documentModalJob.initialIndexQuery,
    onChange = _ref.onChange,
    userID = _ref.userID,
    path = _ref.path;
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    selectedDocs = _useState2[0],
    setSelectedDocs = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    showModal = _useState4[0],
    setShowModal = _useState4[1];

  // there are code paths that can dismiss the modal before the state has been updated, this keeps
  // us from having a memory leak. Keeps us from updating a component that's no longer visible
  useEffect(() => () => {
    setShowModal(false);
  }, []);
  const filterByJob = () => {
    const filters = jobID !== null ? {
      jobs: [{
        entity: 'jobs',
        id: jobID
      }]
    } : {};
    documentModalJobActions.serverFilter(filters, initialIndexQuery);
  };
  const hideModal = () => {
    if (progress !== -1 && progress !== 100) {
      return;
    }
    documentModalJobActions.uploadClear();
    setSelectedDocs([]);

    // there are code paths that can dismiss the modal before the state has been updated, this keeps
    // us from having a memory leak. Keeps us from updating a component that's no longer visible
    setShowModal(false);
  };
  const showModalFunc = () => {
    filterByJob();
    setSelectedDocs([]);
    setShowModal(true);
  };
  const handleAdd = async () => {
    await addDocs(onChange, selectedDocs, userID, path);
    // clear the selection and hide the modal
    hideModal();
  };
  const handleDocumentSelection = function handleDocumentSelection(docs) {
    let uploaded = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (uploaded) {
      // if a document was uploaded, filterByJob again to update the `document.list` values
      filterByJob();
    }
    setSelectedDocs(docs);
  };
  let disabled = false;
  let className = 'tqc-control t-rb-ardb';
  if (selectedDocs.length === 0) {
    disabled = true;
    className += '-disabled';
  }
  const classes = classNames({
    't-rb-adb': true,
    [kebabCase(testingIdentifier)]: true,
    'tqc-control': true
  });
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    block: true,
    className: classes,
    onClick: showModalFunc,
    disabled: !enabled
  }, title), /*#__PURE__*/React.createElement(Modal, {
    isOpen: showModal,
    toggle: hideModal
  }, /*#__PURE__*/React.createElement(ModalHeader, null, /*#__PURE__*/React.createElement("span", {
    className: "t-rb-m-h"
  }, "Select a Library Document to associate")), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(DocumentsUploadPicker, {
    document: documentModalJob,
    documentActions: documentModalJobActions,
    jobID: jobID,
    multiple: true,
    onChange: handleDocumentSelection,
    options: list,
    selectedValues: selectedDocs,
    userID: userID
  })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    disabled: disabled,
    className: className,
    color: "primary",
    onClick: handleAdd
  }, "Add Library Document Association"), /*#__PURE__*/React.createElement(Button, {
    onClick: hideModal,
    disabled: !(progress === -1 || progress === 100),
    className: "t-rb-adb-m-c"
  }, "Close"))));
};
AddDocumentButton.propTypes = {
  enabled: PropTypes.bool.isRequired,
  documentModalJob: PropTypes.shape({
    progress: PropTypes.number,
    initialIndexQuery: PropTypes.shape({
      initialized: PropTypes.bool
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  documentModalJobActions: PropTypes.shape({
    serverFilter: PropTypes.func,
    uploadClear: PropTypes.func
  }).isRequired,
  // document redux actions
  jobID: PropTypes.string,
  path: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  userID: PropTypes.string.isRequired,
  testingIdentifier: PropTypes.string
};
export { AddDocumentButton as UncomposedAddDocumentButton, addDocs };
export default entityComposition(AddDocumentButton, ['documentModalJob'], {
  enabled: enabledSelector,
  jobID: state => state.report.active.jobID !== undefined ? state.report.active.jobID : null,
  userID: state => state.login.user.user_id,
  path: newPathSelector
});